import React, { useState } from 'react';
import { 
  Bell, 
  CheckCircle2, 
  Clock, 
  Calendar, 
  Sparkles, 
  ShieldCheck, 
  Volume2 
} from 'lucide-react';
import { UserProfile, NotificationSetting } from '../types';

interface NotificationSettingsProps {
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  userProfile,
  setUserProfile
}) => {
  const [notificationState, setNotificationState] = useState<NotificationSetting>({
    enabled: true,
    dailyGoalMinutes: userProfile.dailyGoalMinutes || 30,
    reminderTime: '08:00',
    pushGranted: false,
    targetExamDate: '2026-09-15'
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const requestBrowserPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationState(prev => ({ ...prev, pushGranted: true, enabled: true }));
        showTestNotification('Push Notifications Enabled! You will receive daily PRAXIS drill reminders at ' + notificationState.reminderTime);
      } else {
        alert('Notification permission was denied in browser settings.');
      }
    } else {
      alert('Browser does not support desktop notifications.');
    }
  };

  const showTestNotification = (msg: string) => {
    setToastMessage(msg);

    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification('PRAXIS Prep Pro Goal Alert', {
          body: msg,
          icon: '/favicon.ico'
        });
      } catch (err) {
        console.warn('Notification error:', err);
      }
    }

    setTimeout(() => {
      setToastMessage(null);
    }, 6000);
  };

  const handleSaveSettings = () => {
    setUserProfile(prev => ({
      ...prev,
      dailyGoalMinutes: notificationState.dailyGoalMinutes
    }));
    showTestNotification('Settings saved! Daily study reminder scheduled for ' + notificationState.reminderTime);
  };

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="pb-6 border-b border-slate-200">
        <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider">
          <Bell className="w-4 h-4" /> Goal Reminders & Push Notifications
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
          Daily Study Schedule & Goal Notifications
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Configure automated daily push notifications and countdown alarms to keep your study streak consistent until exam day.
        </p>
      </div>

      {/* Simulated Active Notification Toast Banner */}
      {toastMessage && (
        <div className="p-4 rounded-2xl bg-slate-900 text-white shadow-xl border border-indigo-500/40 flex items-center justify-between gap-4 animate-bounce">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shrink-0">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-xs text-amber-400">PRAXIS Daily Goal Reminder</div>
              <div className="text-xs text-slate-200">{toastMessage}</div>
            </div>
          </div>
          <button onClick={() => setToastMessage(null)} className="text-xs text-slate-400 hover:text-white">
            ✕
          </button>
        </div>
      )}

      {/* Main Settings Card */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
        {/* Permission Request Row */}
        <div className="bg-indigo-50/80 border border-indigo-200/80 p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              Browser Push Notifications
            </div>
            <p className="text-xs text-slate-600">
              Allow browser push permissions to receive daily drill alerts even when the app is in the background.
            </p>
          </div>

          <button
            onClick={requestBrowserPermission}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition shadow-xs whitespace-nowrap"
          >
            {notificationState.pushGranted ? '✓ Push Permission Active' : 'Enable Browser Push Alerts'}
          </button>
        </div>

        {/* Form Controls */}
        <div className="space-y-6">
          {/* Daily Goal Slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs font-bold text-slate-800">
              <span>Daily Study Time Goal</span>
              <span className="text-indigo-600">{notificationState.dailyGoalMinutes} Minutes / Day</span>
            </div>

            <input
              type="range"
              min={15}
              max={90}
              step={15}
              value={notificationState.dailyGoalMinutes}
              onChange={(e) => setNotificationState(prev => ({ ...prev, dailyGoalMinutes: Number(e.target.value) }))}
              className="w-full accent-indigo-600 cursor-pointer"
            />

            <div className="flex justify-between text-[11px] text-slate-400 font-medium">
              <span>15 mins (Light)</span>
              <span>30 mins (Recommended)</span>
              <span>60 mins (Intense)</span>
              <span>90 mins (Sprint)</span>
            </div>
          </div>

          {/* Preferred Time & Exam Date Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
            <div className="space-y-1.5">
              <label className="font-bold text-xs text-slate-800 block flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-indigo-600" /> Daily Reminder Schedule
              </label>
              <input
                type="time"
                value={notificationState.reminderTime}
                onChange={(e) => setNotificationState(prev => ({ ...prev, reminderTime: e.target.value }))}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs sm:text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-[11px] text-slate-500">You will receive an automated daily question prompt at this time.</p>
            </div>

            <div className="space-y-1.5">
              <label className="font-bold text-xs text-slate-800 block flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-indigo-600" /> Targeted PRAXIS Exam Date
              </label>
              <input
                type="date"
                value={notificationState.targetExamDate}
                onChange={(e) => setNotificationState(prev => ({ ...prev, targetExamDate: e.target.value }))}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs sm:text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-[11px] text-slate-500">System calculates readiness velocity based on days remaining.</p>
            </div>
          </div>
        </div>

        {/* Save & Test Buttons */}
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => showTestNotification('Sample PRAXIS Drill Alert: Time for your 5-min Core Math question!')}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center gap-1.5"
          >
            <Volume2 className="w-4 h-4 text-slate-500" /> Send Test Alert Toast
          </button>

          <button
            onClick={handleSaveSettings}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-6 py-2.5 rounded-xl transition shadow-xs"
          >
            Save Goal Preferences
          </button>
        </div>
      </div>
    </div>
  );
};
